import type { AxiosRequestConfig } from "axios";

import apiClient from "./client";
import { api } from "./typed";
import type {
  ConsultantApplicationStart,
  SessionStartResponse,
  ResumeSessionResponse,
  EmailVerificationRequest,
  EmailVerificationResponse,
  ResendCodeRequest,
  CodeResendResponse,
  ApplicationStatusResponse,
  ConsultantApplicationStepData,
  ApplicationSubmissionResponse,
  ApplicationSubmitRequest,
  CvUploadResponse,
  SignupConsentsResponse,
  Skill,
} from "@/types/consultant-application";
import {
  SKILL_SEARCH_LIMIT,
  type PublicSkillSearchResult,
  type SkillSuggestion,
  type SkillSuggestionOutcome,
} from "./skills";

/**
 * Public Consultant Application API Client
 * Handles all endpoints for the public consultant sign-up flow
 */
export const consultantApplicationApi = {
  /**
   * Start a new consultant application
   * POST /public/consultant-application/start
   */
  startApplication: async (data: ConsultantApplicationStart): Promise<SessionStartResponse> => {
    return api.post("/public/consultant-application/start", { body: data });
  },

  /**
   * Verify email with the 6-digit code
   * POST /public/consultant-application/verify-email
   */
  verifyEmail: async (data: EmailVerificationRequest): Promise<EmailVerificationResponse> => {
    return api.post("/public/consultant-application/verify-email", { body: data });
  },

  /**
   * Resend verification code
   * POST /public/consultant-application/resend-code
   */
  resendCode: async (data: ResendCodeRequest): Promise<CodeResendResponse> => {
    // The endpoint declares no response model; keep the curated shape.
    return (await api.post("/public/consultant-application/resend-code", {
      body: data,
    })) as unknown as CodeResendResponse;
  },

  /**
   * Get application status and current data
   * GET /public/consultant-application/session/{session_id}
   */
  getApplicationStatus: async (sessionId: string): Promise<ApplicationStatusResponse> => {
    return api.get("/public/consultant-application/session/{session_id}", {
      path: { session_id: sessionId },
    });
  },

  /**
   * Exchange the resume token from a reminder email for the signup session
   * POST /public/consultant-application/resume (backend#901)
   *
   * The token is single-purpose and only works while the application is an
   * unfinished draft; any failure is a 404. The toast is suppressed because
   * the wizard reports an expired link itself.
   */
  resumeApplication: async (token: string): Promise<ResumeSessionResponse> => {
    return api.post("/public/consultant-application/resume", {
      body: { token },
      config: { suppressErrorToast: true },
    });
  },

  /**
   * Update specific step data
   * PUT /public/consultant-application/session/{session_id}/step/{step_number}
   */
  updateApplicationStep: async (
    sessionId: string,
    stepNumber: number,
    data: ConsultantApplicationStepData
  ): Promise<{ success: boolean; message: string }> => {
    // Response declares no model; keep the curated shape.
    return (await api.put(
      "/public/consultant-application/session/{session_id}/step/{step_number}",
      {
        path: { session_id: sessionId, step_number: stepNumber },
        body: { step_data: data },
      }
    )) as { success: boolean; message: string };
  },

  /**
   * Submit completed application
   * POST /public/consultant-application/session/{session_id}/submit
   */
  submitApplication: async (
    sessionId: string,
    acknowledgements: ApplicationSubmitRequest
  ): Promise<ApplicationSubmissionResponse> => {
    // The backend rejects a submit without both acknowledgements (422) or with
    // a version that is no longer current (409) — backend#863.
    return api.post("/public/consultant-application/session/{session_id}/submit", {
      path: { session_id: sessionId },
      body: acknowledgements,
    });
  },

  /**
   * The acknowledgements required on the last signup step (backend#863)
   * GET /public/consultant-application/consents
   */
  getSignupConsents: async (): Promise<SignupConsentsResponse> => {
    return api.get("/public/consultant-application/consents");
  },

  /**
   * Get available skills for selection
   * GET /public/consultant-application/skills
   */
  getAvailableSkills: async (): Promise<Skill[]> => {
    // The endpoint declares no response model; it returns {id, name} rows.
    return (await api.get("/public/consultant-application/skills")) as Skill[];
  },

  /**
   * Ranked skill search over names and aliases, for the signup picker
   * (backend#992). The server ranks and filters; never re-filter by name, that
   * would drop the alias matches.
   * GET /public/consultant-application/skills?search=&limit=
   */
  searchSkills: async (query: string): Promise<PublicSkillSearchResult[]> => {
    return api.get("/public/consultant-application/skills", {
      query: { search: query, limit: SKILL_SEARCH_LIMIT },
    });
  },

  /**
   * Suggest a skill the catalogue lacks; an admin reviews it (backend#992).
   * POST /public/consultant-application/session/{session_id}/skill-suggestions
   */
  suggestSkill: async (sessionId: string, text: string): Promise<SkillSuggestionOutcome> => {
    return api.post("/public/consultant-application/session/{session_id}/skill-suggestions", {
      path: { session_id: sessionId },
      body: { text },
    });
  },

  /**
   * The applicant's suggestions still awaiting review.
   * GET /public/consultant-application/session/{session_id}/skill-suggestions
   */
  getSkillSuggestions: async (sessionId: string): Promise<SkillSuggestion[]> => {
    return api.get("/public/consultant-application/session/{session_id}/skill-suggestions", {
      path: { session_id: sessionId },
      query: { status: "pending" },
      config: { suppressErrorToast: true },
    });
  },

  /**
   * Upload a CV document for the application
   * POST /public/consultant-application/upload-cv
   *
   * The explicit multipart content type is REQUIRED (#782): `apiClient`
   * defaults to `application/json`, and axios then runs `formDataToJSON` on a
   * FormData body — both fields were destroyed and the backend rejected every
   * upload with 422 "Field required". Declaring multipart makes axios keep
   * the FormData and set the boundary (same fix as `photos.ts`, #776).
   *
   * `suppressErrorToast` is for callers that report a failure themselves
   * (the admin Add Joiner dialog, backend#885).
   */
  uploadCv: async (
    sessionId: string,
    file: File,
    options: { suppressErrorToast?: boolean } = {}
  ): Promise<CvUploadResponse> => {
    const formData = new FormData();
    formData.append("session_id", sessionId);
    formData.append("file", file);
    const config: AxiosRequestConfig & { suppressErrorToast?: boolean } = {
      headers: { "Content-Type": "multipart/form-data" },
      suppressErrorToast: options.suppressErrorToast,
    };
    const response = await apiClient.post<CvUploadResponse>(
      `/public/consultant-application/upload-cv`,
      formData,
      config
    );
    return response.data;
  },
};

/**
 * Helper functions for session management
 */
export const sessionManager = {
  SESSION_STORAGE_KEY: "consultant_application_session",

  /**
   * Store session ID in browser storage
   */
  setSessionId: (sessionId: string): void => {
    sessionStorage.setItem(sessionManager.SESSION_STORAGE_KEY, sessionId);
  },

  /**
   * Get session ID from browser storage
   */
  getSessionId: (): string | null => {
    return sessionStorage.getItem(sessionManager.SESSION_STORAGE_KEY);
  },

  /**
   * Remove session ID from browser storage
   */
  removeSessionId: (): void => {
    sessionStorage.removeItem(sessionManager.SESSION_STORAGE_KEY);
  },

  /**
   * Check if there's an active session
   */
  hasActiveSession: (): boolean => {
    return sessionManager.getSessionId() !== null;
  },
};
